/*!
 * Almagrove Jewellers — Shopify theme
 * Handcrafted by Sector 7 · https://sector7hq.co
 * © 2026 Sector7HQ Ltd. All rights reserved.
 *
 * alm-collection-filters.js
 * Wires up the collection-page facets:
 *   1. Auto-submit the facet form on any checkbox / radio change
 *   2. Show-more toggle for filters with > 8 values
 *   3. Dual-thumb draggable price range slider, kept in sync with hidden form
 *      inputs and the visible min/max number boxes. Form submit on change-end.
 *
 * Loaded via /assets/alm-collection-filters.js with `defer`.
 */
(function () {
  'use strict';

  // ---------- helpers ----------
  function debounce(fn, ms) {
    var t = null;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms);
    };
  }

  function clamp(n, min, max) {
    if (n < min) return min;
    if (n > max) return max;
    return n;
  }

  // ---------- AJAX swap engine ----------
  // Replaces the swappable regions (sidebar, main, chips) without a full page
  // reload, so filtering feels instant. Falls back to a real submit if anything
  // goes sideways.
  var inflightSwap = null;

  function setLoading(on) {
    var section = document.querySelector('[data-alm-coll]');
    if (!section) return;
    section.classList.toggle('is-loading', !!on);
  }

  function swapTo(url) {
    if (inflightSwap && inflightSwap.abort) inflightSwap.abort();
    var ctl = new AbortController();
    inflightSwap = ctl;
    setLoading(true);
    return fetch(url, { signal: ctl.signal, headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        // Swap sidebar facets
        var newSidebar = doc.querySelector('[data-alm-coll-sidebar]');
        var oldSidebar = document.querySelector('[data-alm-coll-sidebar]');
        if (newSidebar && oldSidebar) oldSidebar.replaceWith(newSidebar);

        // Swap main (chips + toolbar + grid + pagination + empty state)
        var newMain = doc.querySelector('[data-alm-coll-main]');
        var oldMain = document.querySelector('[data-alm-coll-main]');
        if (newMain && oldMain) oldMain.replaceWith(newMain);

        // Update URL
        try { window.history.pushState({}, '', url); } catch (e) {}
        // Re-bind the new DOM
        bindAutoSubmit();
        bindShowMore();
        bindPriceSliders();
        bindAjaxIntercepts();
        // Reset mobile-bound flag so the new section gets re-bound
        var sec = document.querySelector('[data-alm-coll]');
        if (sec) sec.__almMobileBound = false;
        bindMobile();
        // Inform the rest of the page (wishlist hearts, etc.) so they can re-sync
        document.dispatchEvent(new CustomEvent('almagrove:collection:swapped'));
        // Scroll the grid back into view if user is below it
        var section = document.querySelector('[data-alm-coll]');
        if (section) {
          var rect = section.getBoundingClientRect();
          if (rect.top < 0) window.scrollTo({ top: window.scrollY + rect.top - 80, behavior: 'smooth' });
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Hard fall back — full navigation
        window.location.href = url;
      })
      .then(function () { setLoading(false); });
  }

  function buildUrlFromForm(form) {
    var data = new FormData(form);
    var params = new URLSearchParams();
    for (var pair of data.entries()) {
      var key = pair[0]; var val = pair[1];
      if (val === '' || val == null) continue;
      params.append(key, val);
    }
    var path = window.location.pathname;
    var qs = params.toString();
    return qs ? (path + '?' + qs) : path;
  }

  function bindAjaxIntercepts() {
    var section = document.querySelector('[data-alm-coll]');
    if (!section) return;
    if (section.__almAjaxBound) return;
    section.__almAjaxBound = true;

    section.addEventListener('click', function (ev) {
      // Active filter chip × removal
      var chip = ev.target.closest('[data-chip-remove]');
      if (chip && chip.tagName === 'A') {
        ev.preventDefault();
        swapTo(chip.getAttribute('href'));
        return;
      }
      // "Clear all"
      var clearAll = ev.target.closest('.alm-coll__chips-clear, .alm-coll__filter-clear');
      if (clearAll && clearAll.tagName === 'A') {
        ev.preventDefault();
        swapTo(clearAll.getAttribute('href'));
        return;
      }
      // Pagination
      var pageLink = ev.target.closest('.alm-coll__pagination-arrow, .alm-coll__pagination-num');
      if (pageLink && pageLink.tagName === 'A') {
        ev.preventDefault();
        swapTo(pageLink.getAttribute('href'));
        return;
      }
    });

    // Sort dropdown
    var sortForm = document.getElementById('AlmCollSort');
    if (sortForm && !sortForm.__almAjaxBound) {
      sortForm.__almAjaxBound = true;
      sortForm.addEventListener('submit', function (ev) {
        ev.preventDefault();
        swapTo(buildUrlFromForm(sortForm));
      });
    }
    // Browser back/forward
    if (!window.__almCollPopBound) {
      window.__almCollPopBound = true;
      window.addEventListener('popstate', function () {
        swapTo(window.location.pathname + window.location.search);
      });
    }
  }

  // ---------- 1. auto-submit facet form ----------
  function bindAutoSubmit() {
    var form = document.getElementById('AlmCollFacets');
    if (!form) return;
    if (form.__almSubmitBound) return;
    form.__almSubmitBound = true;
    // All filter changes are held locally — Apply button is the only submit trigger.
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      // Flush any price-box values that haven't blurred yet into the hidden fields
      form.querySelectorAll('[data-alm-price-slider]').forEach(function (root) {
        var numMin  = root.querySelector('[data-price-num-min]');
        var numMax  = root.querySelector('[data-price-num-max]');
        var thumbMin = root.querySelector('[data-price-thumb-min]');
        var thumbMax = root.querySelector('[data-price-thumb-max]');
        var inputMin = root.querySelector('[data-price-input-min]');
        var inputMax = root.querySelector('[data-price-input-max]');
        var dMin = Number(root.getAttribute('data-min'));
        var dMax = Number(root.getAttribute('data-max'));
        if (numMin && thumbMin && inputMin) {
          var lo = clamp(Number(numMin.value) || dMin, dMin, (thumbMax ? Number(thumbMax.value) : dMax) - 1);
          thumbMin.value = String(lo);
          inputMin.value = lo;
        }
        if (numMax && thumbMax && inputMax) {
          var hi = clamp(Number(numMax.value) || dMax, (thumbMin ? Number(thumbMin.value) : dMin) + 1, dMax);
          thumbMax.value = String(hi);
          inputMax.value = hi;
        }
      });
      swapTo(buildUrlFromForm(form));
    });
  }

  // ---------- 2. Show-more toggle ----------
  function bindShowMore() {
    document.querySelectorAll('[data-more-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var list = btn.previousElementSibling;
        if (!list) return;
        var expanded = list.classList.toggle('alm-coll__filter-values--expanded');
        list.classList.toggle('alm-coll__filter-values--collapsed', !expanded);
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        var total = list.querySelectorAll('.alm-coll__filter-value').length;
        btn.textContent = expanded ? '− Show fewer' : '+ Show all (' + total + ')';
      });
    });
  }

  // ---------- 3. Price slider ----------
  function initPriceSlider(root) {
    var min = Number(root.getAttribute('data-min'));
    var max = Number(root.getAttribute('data-max'));
    var thumbMin = root.querySelector('[data-price-thumb-min]');
    var thumbMax = root.querySelector('[data-price-thumb-max]');
    var fill = root.querySelector('[data-price-fill]');
    var displayMin = root.querySelector('[data-price-display-min]');
    var displayMax = root.querySelector('[data-price-display-max]');
    var inputMin = root.querySelector('[data-price-input-min]');
    var inputMax = root.querySelector('[data-price-input-max]');
    var numMin = root.querySelector('[data-price-num-min]');
    var numMax = root.querySelector('[data-price-num-max]');
    var symbol = root.getAttribute('data-currency-symbol') || '£';

    if (!thumbMin || !thumbMax || !fill) return;

    function format(n) {
      return symbol + Number(n).toLocaleString();
    }

    function paint() {
      var lo = Number(thumbMin.value);
      var hi = Number(thumbMax.value);
      var range = max - min || 1;
      var leftPct = ((lo - min) / range) * 100;
      var rightPct = ((hi - min) / range) * 100;
      fill.style.left = leftPct + '%';
      fill.style.right = (100 - rightPct) + '%';
      if (displayMin) displayMin.textContent = format(lo);
      if (displayMax) displayMax.textContent = format(hi);
    }

    function commit() {
      var lo = Number(thumbMin.value);
      var hi = Number(thumbMax.value);
      if (inputMin) inputMin.value = lo;
      if (inputMax) inputMax.value = hi;
      if (numMin) numMin.value = lo;
      if (numMax) numMax.value = hi;
      paint();
    }

    // Live paint while dragging; commit (sync hidden inputs) on release only — no auto-submit
    thumbMin.addEventListener('input', function () {
      var lo = Number(thumbMin.value);
      var hi = Number(thumbMax.value);
      if (lo > hi - 1) thumbMin.value = String(hi - 1);
      paint();
    });
    thumbMax.addEventListener('input', function () {
      var lo = Number(thumbMin.value);
      var hi = Number(thumbMax.value);
      if (hi < lo + 1) thumbMax.value = String(lo + 1);
      paint();
    });
    thumbMin.addEventListener('change', function () { commit(); });
    thumbMax.addEventListener('change', function () { commit(); });

    // Number-box typing → keep slider in sync live; submit only when done (blur or Enter)
    if (numMin) {
      numMin.addEventListener('input', function () {
        var v = clamp(Number(numMin.value || min), min, Number(thumbMax.value) - 1);
        thumbMin.value = String(v);
        paint();
      });
      numMin.addEventListener('change', function () {
        var v = clamp(Number(numMin.value || min), min, Number(thumbMax.value) - 1);
        thumbMin.value = String(v);
        commit();
      });
    }
    if (numMax) {
      numMax.addEventListener('input', function () {
        var v = clamp(Number(numMax.value || max), Number(thumbMin.value) + 1, max);
        thumbMax.value = String(v);
        paint();
      });
      numMax.addEventListener('change', function () {
        var v = clamp(Number(numMax.value || max), Number(thumbMin.value) + 1, max);
        thumbMax.value = String(v);
        commit();
      });
    }

    paint();
  }

  function bindPriceSliders() {
    document.querySelectorAll('[data-alm-price-slider]').forEach(initPriceSlider);
  }

  // ---------- mobile filter drawer + sort sheet ----------
  function bindMobile() {
    var section = document.querySelector('[data-alm-coll]');
    if (!section || section.__almMobileBound) return;
    section.__almMobileBound = true;

    var sidebar = section.querySelector('[data-alm-coll-sidebar]');
    var filterBtn = section.querySelector('[data-alm-mobile-filter]');
    var sortSelect = section.querySelector('[data-alm-mobile-sort-select]');
    var overlay = section.querySelector('[data-alm-mobile-overlay]');

    // Lazily create the overlay so it's a sibling of the sidebar (clean stacking)
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'alm-coll__mobile-overlay';
      overlay.setAttribute('data-alm-mobile-overlay', '');
      section.appendChild(overlay);
    }

    function openDrawer() {
      if (!sidebar) return;
      sidebar.classList.add('is-drawer-open');
      overlay.classList.add('is-active');
      document.body.classList.add('alm-coll-drawer-open');
      if (filterBtn) filterBtn.setAttribute('aria-expanded', 'true');
      var closeBtn = sidebar.querySelector('[data-alm-mobile-filter-close]');
      if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 60);
    }
    function closeDrawer() {
      if (!sidebar) return;
      sidebar.classList.remove('is-drawer-open');
      overlay.classList.remove('is-active');
      document.body.classList.remove('alm-coll-drawer-open');
      if (filterBtn) {
        filterBtn.setAttribute('aria-expanded', 'false');
        filterBtn.focus();
      }
    }

    if (filterBtn) {
      filterBtn.addEventListener('click', function () {
        if (sidebar.classList.contains('is-drawer-open')) closeDrawer();
        else openDrawer();
      });
    }

    section.addEventListener('click', function (ev) {
      if (ev.target.closest('[data-alm-mobile-filter-close]')) {
        // × header button — just close, don't apply
        ev.preventDefault();
        closeDrawer();
      } else if (ev.target.closest('[data-alm-mobile-filter-done]')) {
        // "Show N results" footer button — apply filters then close
        ev.preventDefault();
        closeDrawer();
        var form = document.getElementById('AlmCollFacets');
        if (form) {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      } else if (ev.target.closest('[data-alm-mobile-overlay]')) {
        closeDrawer();
      }
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && sidebar && sidebar.classList.contains('is-drawer-open')) closeDrawer();
    });

    // Mobile sort: a native <select> overlays the Sort button (so a tap reliably
    // opens the OS picker — the desktop sort row is display:none on mobile, so
    // focusing its hidden <select> never worked). On change, re-sort via the AJAX
    // swap, preserving any active filters already present in the URL.
    if (sortSelect && !sortSelect.__almSortBound) {
      sortSelect.__almSortBound = true;
      sortSelect.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        url.searchParams.delete('page'); // re-sorting returns to page 1
        swapTo(url.pathname + url.search);
      });
    }
  }

  // ---------- init ----------
  function init() {
    bindAutoSubmit();
    bindShowMore();
    bindPriceSliders();
    bindAjaxIntercepts();
    bindMobile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
